import React, { useEffect, useState } from 'react';
import { Table, DatePicker, Select, Row, Col, Button, Typography } from 'antd';
import dayjs from 'dayjs';
import { useCommunityEngagement } from "./store";
import { useTemporaryEngagementAssignment } from './store';
import { toast } from 'react-toastify';
import { COMMUNITY_OPTIONS, ENGAGEMENT_TAG_OPTIONS } from "../modules/utilities/utility";
const { Link } = Typography;

const { RangePicker } = DatePicker;
const { Option } = Select;

export const CommunityEngagement = () => {
    const {
        communityEngagementsData,
        communityEngagementLoading,
        pagination,
        fetchCommunityEngagementsData
    } = useCommunityEngagement();

    const {
        communityAssigneeData,
        communityAssigneeLoading,
        fetchEngagementsAssigneeData,
        updateEngagementsAssigneeData
    } = useTemporaryEngagementAssignment();

    const [filters, setFilters] = useState({
        dateRange: [dayjs(), dayjs()],
        tag: null,
        community: null,
        status: null,
        userId: null, // Add userId to filters state
    });

    const [selectedRows, setSelectedRows] = useState([]);
    const [selectedAssignee, setSelectedAssignee] = useState(null);

    const buildFilterPayload = (appliedFilters) => {
        const payload = {};
        if (appliedFilters.tag) payload.tag = appliedFilters.tag;
        if (appliedFilters.status) payload.status = appliedFilters.status;
        if (appliedFilters.userId) payload.user_id = appliedFilters.userId; // Use userId from filters
        if (appliedFilters.community) payload.community = appliedFilters.community;
        if (appliedFilters.dateRange?.length === 2) {
            payload.startDate = appliedFilters.dateRange[0]?.format('YYYY-MM-DD');
            payload.endDate = appliedFilters.dateRange[1]?.format('YYYY-MM-DD');
        }
        return payload;
    };

    const fetchData = (page = 1, pageSize = 10, appliedFilters = filters) => {
        const payload = {
            ...buildFilterPayload(appliedFilters),
            page,
            pageSize,
        };
        fetchCommunityEngagementsData(payload);
    };

    useEffect(() => {
        fetchData();
        fetchEngagementsAssigneeData(); // Fetch assignee data
    }, []);

    const handleTableChange = (pagination) => {
        fetchData(pagination.current, pagination.pageSize, filters);
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const applyFilters = () => {
        fetchData(1, pagination.pageSize, filters);
    };

    const clearFilters = () => {
        const reset = { dateRange: null, tag: null, community: null, status: null, userId: null };
        setFilters(reset);
        fetchData(1, pagination.pageSize, reset);
    };

    const handleRowSelection = (selectedRowKeys, selectedRows) => {
        setSelectedRows(selectedRows);
    };

    const assignAssigneeToSelected = () => {
        if (!selectedAssignee) {
            toast.error('Please select an assignee.');
            return;
        }
        if (selectedRows.length === 0) {
            toast.error('Please select at least one engagement.');
            return;
        }

        const engagementIds = selectedRows.map(row => row._id);

        const payload = {
            repId: selectedAssignee,
            engagementIds,
        };

        updateEngagementsAssigneeData(payload);
    };

    const columns = [
        {
            title: 'Customer',
            dataIndex: 'customer',
            render: val => <Link href={`/lead-details/${val.id}`} target="_blank">
                {val.firstName}
            </Link>,
            fixed: 'left',
            width: 120,
        },
        {
            title: 'Agent',
            dataIndex: ['agent', 'firstName'],
            width: 120,
        },
        {
            title: 'Temp Agent',
            dataIndex: ['temporaryAgent', 'firstName'],
            width: 120,
        },
        {
            title: 'Gender',
            dataIndex: ['customer', 'gender'],
            width: 100,
        },
        {
            title: 'Community',
            dataIndex: ['customer', 'community'],
            width: 150,
        },
        {
            title: 'Tag',
            dataIndex: 'tag',
            width: 160,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            width: 120,
        },
        {
            title: 'Order delivery Date',
            dataIndex: ['customer', 'lastOrderDeliveryDate'],
            render: val => val ? dayjs(val).format('HH:mm YYYY-MM-DD') : '-',
            width: 200,
        },
        {
            title: 'Schedule At',
            dataIndex: 'reminder_date',
            render: val => val ? dayjs(val).format('HH:mm YYYY-MM-DD') : '-',
            width: 160,
        },
        {
            title: 'Created At',
            dataIndex: 'createdAt',
            render: val => val ? dayjs(val).format('HH:mm YYYY-MM-DD') : '-',
            width: 160,
        },
    ];

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col>
                        <RangePicker
                            value={filters.dateRange}
                            onChange={(dates) => handleFilterChange('dateRange', dates)}
                            allowClear
                        />
                    </Col>
                    <Col>
                        <Select
                            placeholder="Filter by Tag"
                            style={{ width: 160 }}
                            allowClear
                            onChange={(value) => handleFilterChange('tag', value)}
                            value={filters.tag}
                        >
                            {ENGAGEMENT_TAG_OPTIONS.map(tag => (
                                <Option key={tag} value={tag}>{tag}</Option>
                            ))}
                        </Select>
                    </Col>
                    <Col>
                        <Select
                            placeholder="Filter by Community"
                            style={{ width: 180 }}
                            allowClear
                            onChange={(value) => handleFilterChange('community', value)}
                            value={filters.community}
                        >
                            {COMMUNITY_OPTIONS.map(comm => (
                                <Option key={comm} value={comm}>{comm}</Option>
                            ))}
                        </Select>
                    </Col>
                    <Col>
                        <Select
                            placeholder="Filter by Status"
                            style={{ width: 180 }}
                            allowClear
                            onChange={(value) => handleFilterChange('status', value)}
                            value={filters.status}
                        >
                            <Option key='active' value='active'>Active</Option>
                            <Option key='inactive' value='inactive'>Inactive</Option>
                        </Select>
                    </Col>
                    <Col>
                        <Select
                            placeholder="Filter by Agent"
                            style={{ width: 300 }}
                            onChange={(value) => handleFilterChange('userId', value)} // Update filters.userId
                            loading={communityAssigneeLoading}
                            allowClear
                            value={filters.userId} // Bind to filters.userId
                        >
                            {communityAssigneeData.map((agent) => (
                                <Option key={agent.user_id} value={agent.user_id}>
                                    {agent.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                    <Col>
                        <Button type="primary" onClick={applyFilters}>Filter</Button>
                    </Col>
                    <Col>
                        <Button onClick={clearFilters}>Clear Filters</Button>
                    </Col>
                </Row>

                {/* Assign Agent to Selected Engagements */}
                <div>
                    <Select
                        placeholder="Select Assignee"
                        style={{ width: 300 }}
                        onChange={(value) => setSelectedAssignee(value)}
                        loading={communityAssigneeLoading}
                        allowClear
                    >
                        {communityAssigneeData.map((agent) => (
                            <Option key={agent.user_id} value={agent.user_id}>
                                {agent.name}
                            </Option>
                        ))}
                    </Select>
                    <Button
                        type="primary"
                        onClick={assignAssigneeToSelected}
                        disabled={communityAssigneeLoading || selectedRows.length === 0 || !selectedAssignee}
                    >
                        Update Assign
                    </Button>
                </div>
            </div>

            <div style={{ overflowX: 'auto' }}>
                <Table
                    rowKey="_id"
                    rowSelection={{
                        type: 'checkbox',
                        onChange: handleRowSelection,
                    }}
                    columns={columns}
                    dataSource={communityEngagementsData}
                    loading={communityEngagementLoading}
                    scroll={{ x: 1300 }}
                    pagination={{
                        current: pagination.page,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                    }}
                    onChange={handleTableChange}
                />
            </div>
        </div>
    );
};